import React, { FC, useMemo } from 'react'
import { Typography } from '@mui/material'
import { ChevronForward } from '@emotion-icons/ionicons-outline/ChevronForward'
import styled from '@emotion/styled'
import { Colors, Shapes } from 'src/components/theme'
import { IconBox } from 'src/v2/components/icon-box'
import { Insight } from 'src/components/insights/types'
import { PathLink } from 'src/components/sidebar-panels/available-insights/insight-icon'
import { useRouter } from 'next/router'
import { isFeatureEnabled } from 'src/utils/feature-flag'
import { AcquisitionHistory } from 'src/v2/views/owner-reveal/insights/acquisition-history'

const Title = styled(Typography)`
  padding: 0 ${Shapes.spacing[4]}px ${Shapes.spacing[4]}px ${Shapes.spacing[4]}px;
`

const ItemRow = styled.div<{ color: string; secondaryColor: string; active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Shapes.spacing[1]}px ${Shapes.spacing[4]}px;
  cursor: pointer;
  gap: ${Shapes.spacing[2]}px;
  background-color: ${(props) => (props.active ? props.secondaryColor : 'transparent')};

  ${(props) =>
    props.active
      ? `
    ${IconBox} {
      color: white;
      background-color: ${props.color};
    }
  `
      : ''}
  :hover {
    background-color: ${(props) => props.secondaryColor};
    * {
      color: ${(props) => props.color};
    }
  }
`

const ItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

export type SidebarInsightsMenuItem = Omit<Insight, 'component' | 'linkTooltipFile' | 'supportedResultTypes'> & {
  color: string
  secondaryColor: string
}

type SidebarInsightsMenuItemProps = {
  item: SidebarInsightsMenuItem
  active?: boolean
}
const SidebarInsightsMenuItem: FC<SidebarInsightsMenuItemProps> = ({ item, active }) => {
  return (
    <ItemRow color={item.color} secondaryColor={item.secondaryColor} active={active}>
      <ItemContent id={item.id}>
        <IconBox size={32} color={item.color} secondaryColor={item.secondaryColor}>
          <item.icon size={18} color={item.color} />
        </IconBox>
        <Typography variant="textmdm" color={active ? item.color : Colors.neutral500}>
          {item.name}
        </Typography>
      </ItemContent>
      <ChevronForward color={active ? item.color : 'rgba(187, 200, 213, 1)'} size={14} />
    </ItemRow>
  )
}

type SidebarInsightsMenuProps = {
  title: string
  items: SidebarInsightsMenuItem[]
}
export const SidebarInsightsMenu: FC<SidebarInsightsMenuProps> = ({ items, title }) => {
  const router = useRouter()
  const showOwnerRevealMockData = isFeatureEnabled('owner-reveal-mock-data')

  const finalItems = useMemo(() => {
    if (showOwnerRevealMockData) {
      return items
    }
    return items.filter((item) => item.id !== AcquisitionHistory.id)
  }, [items, showOwnerRevealMockData])

  return (
    <div>
      <Title variant="title2" color={Colors.neutral950}>
        {title}
      </Title>
      {finalItems.map((item) => (
        <PathLink key={item.id} to={`/owner-reveal/insights/${item.id}`}>
          <SidebarInsightsMenuItem item={item} active={router.asPath.includes(item.id)} />
        </PathLink>
      ))}
    </div>
  )
}
